import { useEffect, useRef, useState } from "react";
import { motion, animate, AnimatePresence } from "framer-motion";
import { CalendarDays, X } from "lucide-react";
import AppointmentForm from "./AppointmentForm";
import { useLang } from "../i18n/i18n";
import { track } from "../lib/track";

const PURITIES = {
  or: [
    { k: "24K", f: 1 },
    { k: "22K", f: 0.917 },
    { k: "18K", f: 0.75 },
    { k: "14K", f: 0.583 },
    { k: "10K", f: 0.417 },
  ],
  argent: [
    { k: "925", f: 0.925 },
    { k: "900", f: 0.9 },
    { k: "800", f: 0.8 },
    { k: "650", f: 0.65 },
    { k: "500", f: 0.5 },
  ],
  platine: [
    { k: "950", f: 0.95 },
    { k: "900", f: 0.9 },
  ],
};

const METAL_KEYS = { or: "calc.gold", argent: "calc.silver", platine: "calc.platinum" };

export default function Calculator({ settings }) {
  const { t, lang } = useLang();
  const locale = { fr: "fr-CA", en: "en-CA", es: "es-ES" }[lang] || "fr-CA";
  const fmt = (v) => v.toLocaleString(locale, { style: "currency", currency: "CAD", maximumFractionDigits: 2 });

  const [metal, setMetal] = useState("or");
  const [purity, setPurity] = useState("18K");
  const [grams, setGrams] = useState(10);
  const [shown, setShown] = useState(0);
  const [showAppt, setShowAppt] = useState(false);

  const s = settings;
  const pricePerG = s
    ? metal === "or"
      ? s.price_per_gram_24k
      : metal === "argent"
        ? s.silver_price_per_gram
        : s.platinum_price_per_gram
    : 0;
  const payout = s
    ? metal === "or"
      ? s.payout_percent
      : metal === "argent"
        ? s.silver_payout_percent
        : s.platinum_payout_percent
    : 0;
  const factor = PURITIES[metal].find((x) => x.k === purity)?.f ?? 1;
  const purityLabel = metal === "or" ? purity : `${purity} ‰`;

  const gross = (grams || 0) * pricePerG * factor;
  const estimate = gross * (payout / 100);
  const metalLabel = `${t(METAL_KEYS[metal])}${metal === "or" ? ` ${purity}` : ` ${purity} ‰`}`;
  const recap = `${metalLabel} · ${grams || 0} ${t("calc.grams")} · ≈ ${fmt(estimate)}`;

  const lastCalc = useRef(0);

  useEffect(() => {
    if (Date.now() - lastCalc.current > 10000) {
      lastCalc.current = Date.now();
      track("calc", metal === "or" ? `or ${purity}` : `${metal} ${purity}`);
    }
  }, [metal, purity, grams]);

  useEffect(() => {
    const c = animate(shown, estimate, {
      duration: 0.7,
      ease: "easeOut",
      onUpdate: (v) => setShown(v),
    });
    return () => c.stop();
  }, [estimate]);

  const live = s ? (metal === "or" ? s.live_available : metal === "argent" ? s.silver_live_available : s.platinum_live_available) : false;

  return (
    <div
      className="relative rounded-3xl border border-gold/20 bg-panel/80 backdrop-blur-xl p-6 sm:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
      data-testid="calculator-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display text-2xl sm:text-3xl text-cream">{t("calc.title")}</h3>
        <span
          className="inline-flex items-center gap-2 rounded-full border border-gold/25 px-3.5 py-1.5 text-[11px] tracking-wide uppercase text-goldlight/90"
          data-testid="calc-live-badge"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-400" : "bg-golddeep"}`} />
          {live ? t("calc.live") : t("calc.ref")}
        </span>
      </div>

      <div className="mt-7 grid gap-8 lg:grid-cols-[1.25fr_1fr] lg:gap-12">
        <div>
          <div className="grid grid-cols-3 gap-2">
            {["or", "argent", "platine"].map((m) => (
              <button
                key={m}
                onClick={() => {
                  setMetal(m);
                  setPurity(m === "or" ? "18K" : m === "argent" ? "925" : "950");
                }}
                data-testid={`calc-metal-${m}`}
                className={`rounded-xl border px-3 py-3 text-sm font-semibold uppercase tracking-widest transition-all ${
                  metal === m
                    ? "border-gold bg-gold/10 text-goldlight"
                    : "border-cream/10 text-mutedc hover:border-gold/40 hover:text-cream"
                }`}
              >
                {t(METAL_KEYS[m])}
              </button>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {PURITIES[metal].map((x) => (
              <button
                key={x.k}
                onClick={() => setPurity(x.k)}
                data-testid={`calc-purity-${x.k.toLowerCase()}`}
                className={`rounded-full border px-4 py-2 text-sm transition-all ${
                  purity === x.k
                    ? "border-gold bg-gold text-ink font-bold"
                    : "border-cream/15 text-cream/70 hover:border-gold/50 hover:text-goldlight"
                }`}
              >
                {metal === "or" ? x.k : `${x.k} ‰`}
              </button>
            ))}
          </div>

          <div className="mt-7">
            <div className="flex items-end justify-between">
              <label className="text-xs uppercase tracking-[0.2em] text-mutedc">{t("calc.weight")}</label>
              <span className="text-sm text-cream/60 tabular">{grams || 0} g</span>
            </div>
            <input
              type="range"
              min="1"
              max="500"
              value={grams}
              onChange={(e) => setGrams(Number(e.target.value))}
              data-testid="calc-grams-range"
              className="mt-3 w-full"
              style={{ ["--fill"]: `${((grams - 1) / 499) * 100}%` }}
            />
            <input
              type="number"
              min="0"
              value={grams}
              onChange={(e) => setGrams(Math.max(0, Number(e.target.value)))}
              data-testid="calc-grams-input"
              className="mt-4 w-36 rounded-lg border border-cream/15 bg-ink/60 px-4 py-3 text-lg text-cream tabular outline-none focus:border-gold"
            />
            <span className="ml-2 text-sm text-mutedc">{t("calc.grams")}</span>
          </div>
        </div>

        <div className="rounded-2xl border border-gold/15 bg-ink/50 p-6 sm:p-8 flex flex-col justify-between">
          <div className="space-y-2.5 text-sm" data-testid="calc-breakdown">
            <div className="flex justify-between text-mutedc">
              <span>{t("calc.metalPrice")}</span>
              <span className="text-cream/80 tabular">{fmt(pricePerG)} / g</span>
            </div>
            <div className="flex justify-between text-mutedc">
              <span>{t("calc.purity")}</span>
              <span className="text-cream/80">{purityLabel}</span>
            </div>
            <div className="flex justify-between text-mutedc">
              <span>{t("calc.gross")}</span>
              <span className="text-cream/80 tabular">{fmt(gross)}</span>
            </div>
            <div className="flex justify-between text-mutedc">
              <span>{t("calc.rate")}</span>
              <span className="text-cream/80">{payout} %</span>
            </div>
          </div>
          <div className="mt-6 border-t border-gold/15 pt-6">
            <p className="text-xs uppercase tracking-[0.25em] text-mutedc">{t("calc.estimate")}</p>
            <p className="mt-2 font-display text-4xl sm:text-5xl text-goldlight tabular" data-testid="calc-estimate-value">
              {fmt(shown)}
            </p>
            <p className="mt-3 text-xs leading-relaxed text-mutedc">{t("calc.disclaimer")}</p>
            <button
              onClick={() => setShowAppt(true)}
              data-testid="calc-cta-appointment"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition-all hover:bg-goldlight hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]"
            >
              <CalendarDays size={15} /> {t("calc.book")}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showAppt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/80 backdrop-blur-sm p-4"
            onClick={() => setShowAppt(false)}
            data-testid="appointment-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl border border-gold/25 bg-panel p-6 sm:p-9 shadow-[0_40px_120px_rgba(0,0,0,0.7)]"
              data-testid="appointment-modal-panel"
            >
              <button
                onClick={() => setShowAppt(false)}
                data-testid="appointment-modal-close"
                aria-label="Fermer"
                className="absolute top-4 right-4 rounded-full border border-cream/15 p-2 text-cream/60 hover:text-goldlight hover:border-gold transition-all"
              >
                <X size={16} />
              </button>
              <h3 className="font-display text-2xl sm:text-3xl text-cream pr-8">{t("apt.formTitle")}</h3>
              <p className="mt-2 text-sm text-mutedc">{t("calc.modalSub")}</p>
              <div className="mt-6">
                <AppointmentForm details={recap} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
